/**
 * Shared HTTP plumbing for write ops in src/sync/ops/*.
 *
 * Each op does the same handful of things: stamp auth + idempotency
 * headers, fire a fetch, categorize the response, and (for ops whose
 * endpoint references a parent CallLog) substitute the resolved
 * calllogid. Factoring those out keeps each op file short and the
 * error-classification logic identical across all 14 ops.
 */

import type { QueueRow, SyncResult } from './types';
import { STORES, getAll } from '../utils/indexedDB';

export const BASE_URL =
  (import.meta.env?.VITE_API_BASE_URL as string | undefined) || 'http://localhost:8000';

export function getAuthToken(): string | null {
  try {
    return localStorage.getItem('accessToken');
  } catch {
    return null;
  }
}

/** Headers for JSON-body requests. Idempotency-Key included when supplied. */
export function jsonHeaders(idempotencyKey?: string): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  return headers;
}

/**
 * Headers for multipart/FormData requests. Crucially, Content-Type is
 * NOT set — the browser inserts it (with the right boundary) when fetch
 * sees a FormData body.
 */
export function multipartHeaders(idempotencyKey?: string): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  return headers;
}

/**
 * Classify an HTTP Response into a SyncResult, returning null for 2xx
 * (op-specific success handling continues). 5xx and 408/401 are
 * retriable; 4xx is terminal.
 */
export async function classifyResponse(response: Response): Promise<SyncResult | null> {
  if (response.status >= 500 || response.status === 0) {
    const text = await response.text().catch(() => '');
    return {
      status: 'failed',
      retriable: true,
      errorMessage: `${response.status}: ${text}`,
    };
  }
  if (response.status === 401 || response.status === 408) {
    return {
      status: 'failed',
      retriable: true,
      errorMessage: `auth/timeout (${response.status})`,
    };
  }
  if (response.status >= 400) {
    const text = await response.text().catch(() => 'Unknown error');
    let message: string | null = null;
    try {
      message = humanizeApiError(JSON.parse(text));
    } catch {
      /* not JSON — leave as raw text */
    }
    return {
      status: 'failed',
      retriable: false,
      errorMessage: message || text || `HTTP ${response.status}`,
    };
  }
  return null;
}

/**
 * #2465 — turn a DRF error body into a readable sentence for the Sync Issues UI.
 * Previously a validation 400 surfaced as raw JSON (e.g.
 * {"non_field_errors":["Call time overlapping…"]} or
 * {"driveodoend":["A valid integer is required."]}) which reps couldn't parse.
 * Handles: plain string, {detail|error|message}, {non_field_errors:[…]}, and
 * field-error maps {field:[…]} (prefixed with the field name).
 */
export function humanizeApiError(parsed: unknown): string | null {
  if (parsed == null) return null;
  if (typeof parsed === 'string') return parsed;
  if (Array.isArray(parsed)) return parsed.map((x) => humanizeApiError(x)).filter(Boolean).join('; ') || null;
  if (typeof parsed !== 'object') return String(parsed);

  const obj = parsed as Record<string, unknown>;
  const direct = obj.detail ?? obj.error ?? obj.message;
  if (typeof direct === 'string' && direct.trim()) return direct;

  if (Array.isArray(obj.non_field_errors)) {
    const m = obj.non_field_errors.map((x) => humanizeApiError(x)).filter(Boolean).join('; ');
    if (m) return m;
  }

  // Field-error map: take the first field with a message, prefix the field name.
  for (const [field, val] of Object.entries(obj)) {
    if (field === 'non_field_errors') continue;
    const m = humanizeApiError(val);
    if (m) return field === 'detail' ? m : `${field}: ${m}`;
  }
  return null;
}

/**
 * Read a Blob or File into an ArrayBuffer. Uses Response as the
 * adapter so this works in jsdom (where File lacks arrayBuffer()) as
 * well as real browsers.
 */
export async function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return await new Response(blob).arrayBuffer();
}

/** Wrap a thrown fetch (TypeError / network drop) into a retriable SyncResult. */
export function networkErrorResult(err: unknown): SyncResult {
  return {
    status: 'failed',
    retriable: true,
    errorMessage: err instanceof Error ? err.message : String(err),
  };
}

/**
 * Resolution states for a parent visit's server-assigned CallLog id.
 *   - number: parent has synced, here is its calllogid
 *   - null: op declared no parent dependency
 *   - undefined: parent declared but not synced yet (sync() should
 *     return status='pending' to leave the op alone this drain)
 */
export async function resolveParentCallLogId(
  parentLocalId: string | undefined | null,
): Promise<number | null | undefined> {
  if (!parentLocalId) return null;
  const rows = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<{
    uuid?: string;
    callLogId?: number | null;
    projectId?: number;
    taskType?: string;
    taskId?: number;
  }>;
  const match = rows.find((r) => r.uuid === parentLocalId);
  if (!match) return undefined;
  if (typeof match.callLogId === 'number') return match.callLogId;
  // #2197 (diag #54 + #55): each pause/resume mints a NEW session uuid and the
  // resume aux-rescue re-keys the dependent's parent onto it (useVisitTask
  // ~L826), but that resumed session's callLogId is null — the real server
  // calllogid is on a SIBLING session of the SAME task (the original check-in).
  // Without this fallback the dependent (photo/survey/order) resolves to
  // `undefined` and the orphan sweep wrongly fails it "Parent visit never
  // synced", losing the rep's work. Borrow the calllogid from any same-task
  // sibling that has synced. Task identity matches the codebase's predicate:
  // projectId + taskType, with taskId lenient for the synthetic task_id=0 path.
  // Only borrow when this session has a real task identity — never match two
  // identity-less sessions to each other.
  if (match.projectId == null || match.taskType == null) return undefined;
  const sibling = rows.find(
    (r) =>
      r.uuid !== parentLocalId &&
      typeof r.callLogId === 'number' &&
      r.projectId === match.projectId &&
      r.taskType === match.taskType &&
      (r.taskId === match.taskId || match.taskId === 0 || r.taskId === 0),
  );
  return sibling ? (sibling.callLogId as number) : undefined;
}

/**
 * For ops whose `endpoint` contains the `{calllogid}` placeholder.
 * Returns either the substituted URL (string) or a SyncResult shaped
 * like the helper wants to short-circuit (pending if parent unsynced,
 * failed if mis-configured). Callers do:
 *
 *   const urlOrShortCircuit = await resolveCallLogUrl(row, parentLocalId);
 *   if (typeof urlOrShortCircuit !== 'string') return urlOrShortCircuit;
 *   const url = urlOrShortCircuit;
 */
export async function resolveCallLogUrl(
  row: QueueRow,
  parentLocalId: string | undefined | null,
): Promise<string | SyncResult> {
  const cid = await resolveParentCallLogId(parentLocalId);
  if (cid === undefined) {
    return {
      status: 'pending',
      retriable: true,
      errorMessage: 'Parent visit has not synced yet',
    };
  }
  if (cid === null) {
    return {
      status: 'failed',
      retriable: false,
      errorMessage: 'Op needs a parent localId but none was supplied',
    };
  }
  return `${BASE_URL}${row.endpoint.replace('{calllogid}', String(cid))}`;
}
